import { clientStorage } from "@/lib/clientStorage";
import React, { useState, useEffect } from "react";
import { 
  Search, Download, FileText, ChevronRight, X, ArrowLeft, 
  RefreshCw, Mail, MailOpen, Paperclip, CheckCircle, Clock, 
  Inbox as InboxIcon, User, MessageSquare, AlertTriangle, ShieldCheck
} from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { createPageUrl } from "@/utils";
import { base44 } from "@/api/base44Client";
import { useAuth } from "@/lib/AuthContext";

// Helper to format dates beautifully
function formatDateTime(dateStr) {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  
  const day = date.getDate();
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "pm" : "am";
  hours = hours % 12;
  hours = hours ? hours : 12;
  
  return `${day} ${month} ${year}, ${hours}:${minutes}${ampm}`;
}

export default function Inbox({ hideHeader = false }) {
  const { user } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [operators, setOperators] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFirm, setSelectedFirm] = useState("All");
  const [selectedPriority, setSelectedPriority] = useState("All");
  const [activeStatusTab, setActiveStatusTab] = useState("New");
  const [activeDetailTab, setActiveDetailTab] = useState("preview"); // preview, audit, comments
  
  // Interactive editing states
  const [commentText, setCommentText] = useState("");
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);

  // Load submissions from API
  const loadSubmissions = async () => {
    setIsLoadingList(true);
    try {
      const filters = {
        status: activeStatusTab,
        clientId: selectedFirm === "All" ? null : selectedFirm.toLowerCase(),
        priority: selectedPriority === "All" ? null : selectedPriority,
        search: searchQuery || null
      };
      const list = await base44.submissions.list(filters);
      setSubmissions(list);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load submissions queue");
    } finally {
      setIsLoadingList(false);
    }
  };

  // Load operators list
  const loadOperators = async () => {
    try {
      const list = await base44.clientAdmin.getOperators();
      setOperators(list);
    } catch (e) {
      console.error("Failed to load operators:", e);
    }
  };

  // Load submission detail
  const loadItemDetail = async (id) => {
    setIsLoadingDetail(true);
    try {
      const detail = await base44.submissions.get(id);
      setSelectedItem(detail);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load submission details");
    } finally {
      setIsLoadingDetail(false);
    }
  };

  useEffect(() => {
    loadSubmissions();
    loadOperators();
  }, [activeStatusTab, selectedFirm, selectedPriority]);

  // Debounced search query trigger
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      loadSubmissions();
    }, 400);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery]);

  // Refresh active selection detail
  const refreshDetail = () => {
    if (selectedItem) {
      loadItemDetail(selectedItem.id);
    }
  };

  // Update Status/Assignee
  const handleUpdateField = async (fields) => {
    if (!selectedItem) return;
    try {
      toast.loading("Saving changes...", { id: "saving-sub" });
      await base44.submissions.update(selectedItem.id, fields);
      toast.success("Submission updated successfully!", { id: "saving-sub" });
      
      // Update local item details & reload main list
      await loadItemDetail(selectedItem.id);
      loadSubmissions();
    } catch (e) {
      console.error(e);
      toast.error("Failed to update submission details.", { id: "saving-sub" });
    }
  };

  // Add Comment
  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim() || !selectedItem) return;

    setIsSubmittingComment(true);
    try {
      await base44.submissions.addComment(selectedItem.id, commentText);
      setCommentText("");
      toast.success("Comment added successfully");
      await loadItemDetail(selectedItem.id);
    } catch (e) {
      console.error(e);
      toast.error("Failed to add comment");
    } finally {
      setIsSubmittingComment(false);
    }
  };

  // Download file securely with Bearer Token
  const handleDownloadFile = async (att) => {
    try {
      toast.loading(`Downloading ${att.filename}...`, { id: "dl-file" });
      const token = clientStorage.getItem("clsp_session_token");
      const res = await window.fetch(att.storage_path, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = att.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Download complete!", { id: "dl-file" });
    } catch (e) {
      console.error(e);
      toast.error("Failed to download file payload.", { id: "dl-file" });
    }
  };

  // Status Badge styles
  const getStatusBadgeStyle = (status) => {
    switch (status) {
      case "New":
        return "bg-blue-100 text-blue-800 dark:bg-blue-950/40 dark:text-blue-300 border border-blue-200 dark:border-blue-900/40";
      case "Assigned":
        return "bg-purple-100 text-purple-800 dark:bg-purple-950/40 dark:text-purple-300 border border-purple-200 dark:border-purple-900/40";
      case "In Progress":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-950/40 dark:text-yellow-300 border border-yellow-200 dark:border-yellow-900/40";
      case "Waiting":
        return "bg-orange-100 text-orange-800 dark:bg-orange-950/40 dark:text-orange-300 border border-orange-200 dark:border-orange-900/40";
      case "Completed":
        return "bg-green-100 text-green-800 dark:bg-green-950/40 dark:text-green-300 border border-green-200 dark:border-green-900/40";
      case "Archived":
      default:
        return "bg-slate-100 text-slate-800 dark:bg-slate-900 dark:text-slate-400 border border-slate-200 dark:border-slate-800";
    }
  };

  // Priority Badge styles
  const getPriorityBadgeStyle = (priority) => {
    switch (priority) {
      case "Urgent":
        return "bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-400 border border-red-200 dark:border-red-900/40 font-bold";
      case "High":
        return "bg-orange-100 text-orange-800 dark:bg-orange-950/40 dark:text-orange-300 border border-orange-200 dark:border-orange-900/40 font-bold";
      case "Normal":
      default:
        return "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-350 border border-slate-200 dark:border-slate-700";
    }
  };

  // Firm Theme styles
  const getFirmStyle = (firm) => {
    const code = (firm || "").toUpperCase();
    switch (code) {
      case "LACW":
        return {
          bg: "bg-purple-50 text-purple-700 dark:bg-purple-950/40 dark:text-purple-300 border-purple-100 dark:border-purple-900/50",
          badge: "bg-purple-600 hover:bg-purple-700 text-white",
          text: "text-purple-600 dark:text-purple-400",
          border: "border-purple-200 dark:border-purple-800"
        };
      case "VALS":
        return {
          bg: "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300 border-red-100 dark:border-red-900/50",
          badge: "bg-[#8C1D18] hover:bg-[#721512] text-white",
          text: "text-[#8C1D18] dark:text-[#a13b35]",
          border: "border-[#8C1D18]/20 dark:border-[#8C1D18]/50"
        };
      case "RGA":
      case "ROBYN GREENSILL":
        return {
          bg: "bg-teal-50 text-teal-700 dark:bg-teal-950/40 dark:text-teal-305 border-teal-105 dark:border-teal-900/50",
          badge: "bg-teal-600 hover:bg-teal-755 text-white",
          text: "text-teal-600 dark:text-teal-400",
          border: "border-teal-200 dark:border-teal-800"
        };
      default:
        return {
          bg: "bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300 border-blue-100 dark:border-blue-900/50",
          badge: "bg-blue-600 hover:bg-blue-700 text-white",
          text: "text-blue-600 dark:text-blue-400",
          border: "border-blue-200 dark:border-blue-800"
        };
    }
  };

  // Get dynamic countdown label
  const getSLACountdown = (slaStr) => {
    if (!slaStr) return "N/A";
    const due = new Date(slaStr);
    const now = new Date();
    const diffMs = due - now;
    if (diffMs < 0) {
      return (
        <span className="text-red-600 dark:text-red-400 font-extrabold flex items-center gap-1">
          <AlertTriangle className="w-3.5 h-3.5" /> OVERDUE
        </span>
      );
    }
    const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
    if (diffHrs < 1) {
      const diffMins = Math.floor(diffMs / (1000 * 60));
      return <span className="text-orange-500 font-extrabold">{diffMins}m remaining</span>;
    }
    if (diffHrs < 24) {
      return <span className="text-orange-400 font-bold">{diffHrs}h remaining</span>;
    }
    const diffDays = Math.floor(diffHrs / 24);
    return <span className="text-slate-600 dark:text-slate-400 font-medium">{diffDays} days remaining</span>;
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex flex-col font-sans">
      {/* Header */}
      {!hideHeader && (
        <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-6 py-4 flex items-center justify-between sticky top-0 z-[10] shadow-sm transition-colors duration-200">
          <div className="flex items-center gap-3">
            <a
              href={createPageUrl("Home")}
              className="p-2 text-slate-500 hover:text-purple-600 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-all"
              title="Back to Dashboard"
            >
              <ArrowLeft className="w-5 h-5" />
            </a>
            <div>
              <h1 className="text-xl font-bold text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
                <Mail className="w-5 h-5 text-purple-650" />
                Universal Submission Gateway
              </h1>
              <p className="text-slate-500 dark:text-slate-400 text-xs mt-0.5">Central portal inbox ingested via Outlook, Gmail, Web & REST APIs</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                loadSubmissions();
                toast.success("Inbox queue refreshed");
              }}
              className="p-2 text-slate-500 hover:text-purple-600 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-all flex items-center gap-1.5 text-xs font-bold border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm"
            >
              <RefreshCw className={cn("w-4 h-4", isLoadingList && "animate-spin")} />
              Refresh
            </button>
          </div>
        </header>
      )}

      {/* Status Bar Tabs (Extended Inbox View Filter) */}
      <div className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-6 py-1.5 flex items-center gap-2 overflow-x-auto shadow-sm">
        {["New", "Assigned", "In Progress", "Waiting", "Completed", "Archived"].map((tab) => (
          <button
            key={tab}
            onClick={() => {
              setActiveStatusTab(tab);
              setSelectedItem(null);
            }}
            className={cn(
              "px-4 py-2 text-xs font-bold rounded-xl border transition-all flex items-center gap-1.5 whitespace-nowrap",
              activeStatusTab === tab
                ? "bg-purple-600 border-purple-600 text-white shadow-sm"
                : "bg-white dark:bg-slate-800 text-slate-655 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-750"
            )}
          >
            {tab}
            <Badge className={cn("text-[10px] px-1.5 py-0 border-0", activeStatusTab === tab ? "bg-purple-700 text-white" : "bg-slate-100 text-slate-500")}>
              {submissions.filter(s => s.status === tab).length || 0}
            </Badge>
          </button>
        ))}
      </div>

      {/* Main Layout: Master-Detail Split Screen */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left pane: Submissions List */}
        <div className="w-full md:w-[380px] lg:w-[420px] bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 flex-shrink-0 flex flex-col overflow-hidden transition-colors duration-200">
          
          {/* Filters Block */}
          <div className="p-4 border-b border-slate-200 dark:border-slate-800 space-y-3 bg-slate-50/50 dark:bg-slate-900/50">
            <div className="relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
              <input
                type="text"
                placeholder="Search subject, body or sender..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-slate-200 dark:border-slate-700 rounded-xl text-sm bg-white dark:bg-slate-800 text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-650 transition-all shadow-sm"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-3 text-slate-400 hover:text-slate-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            <div className="flex items-center gap-3">
              <div className="flex-1">
                <select
                  value={selectedFirm}
                  onChange={(e) => setSelectedFirm(e.target.value)}
                  className="w-full text-xs bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-2.5 py-1.5 font-bold text-slate-700 dark:text-slate-200 shadow-sm"
                >
                  <option value="All">All Clients</option>
                  <option value="LACW">LACW</option>
                  <option value="VALS">VALS</option>
                  <option value="RGA">Robyn Greensill</option>
                </select>
              </div>

              <div className="flex-1">
                <select
                  value={selectedPriority}
                  onChange={(e) => setSelectedPriority(e.target.value)}
                  className="w-full text-xs bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-2.5 py-1.5 font-bold text-slate-700 dark:text-slate-200 shadow-sm"
                >
                  <option value="All">All Priorities</option>
                  <option value="Urgent">Urgent</option>
                  <option value="High">High</option>
                  <option value="Normal">Normal</option>
                </select>
              </div>
            </div>
          </div>

          {/* List Content */}
          <div className="flex-1 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800/60">
            {isLoadingList ? (
              <div className="flex justify-center items-center py-20">
                <div className="w-6 h-6 border-2 border-slate-200 border-t-purple-600 rounded-full animate-spin"></div>
              </div>
            ) : submissions.length > 0 ? (
              submissions.map((item) => {
                const isSelected = selectedItem?.id === item.id;
                const clientCode = item.client_id?.toUpperCase();
                const firmStyle = getFirmStyle(clientCode);
                return (
                  <div
                    key={item.id}
                    onClick={() => loadItemDetail(item.id)}
                    className={cn(
                      "p-4 cursor-pointer transition-all hover:bg-slate-50/50 dark:hover:bg-slate-800/20 relative flex gap-3 group border-l-4",
                      isSelected 
                        ? "bg-purple-50/50 dark:bg-purple-950/15 border-l-purple-650" 
                        : "border-l-transparent"
                    )}
                  >
                    <div className="flex-1 min-w-0 space-y-1.5">
                      <div className="flex items-center justify-between gap-2">
                        <span className={cn("text-[9px] font-bold px-2 py-0.5 rounded-full uppercase", firmStyle.bg)}>
                          {clientCode}
                        </span>
                        <span className="text-[10px] text-slate-400 dark:text-slate-500 font-medium">
                          {formatDateTime(item.created_at).split(",")[0]}
                        </span>
                      </div>
                      
                      <h3 className={cn(
                        "text-[13px] font-bold truncate transition-colors",
                        isSelected ? "text-purple-700 dark:text-purple-400" : "text-slate-800 dark:text-slate-200"
                      )}>
                        {item.subject || "No Subject"}
                      </h3>
                      
                      <p className="text-xs text-slate-400 dark:text-slate-500 line-clamp-1 leading-relaxed">
                        {item.body || "No text body"}
                      </p>

                      <div className="flex items-center justify-between gap-2 pt-1">
                        <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-850 px-2 py-0.5 rounded-md">
                          {item.task_type_name || "General Request"}
                        </span>

                        <span className={cn("text-[9px] px-1.5 py-0.5 rounded-full font-bold", getPriorityBadgeStyle(item.priority))}>
                          {item.priority}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex flex-col justify-center items-end flex-shrink-0">
                      <ChevronRight className={cn(
                        "w-4 h-4 transition-transform", 
                        isSelected ? "text-purple-500 translate-x-1" : "text-slate-300 dark:text-slate-700 group-hover:text-slate-400"
                      )} />
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="p-8 text-center space-y-3">
                <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto text-slate-400">
                  <InboxIcon className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-slate-755 dark:text-slate-350">No Submissions Found</h4>
                  <p className="text-xs text-slate-400 dark:text-slate-500 mt-1 max-w-[200px] mx-auto">
                    No submissions match your active filter/search criteria.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right pane: Submission Detail View */}
        <div className="flex-1 bg-slate-50 dark:bg-slate-950 overflow-y-auto flex flex-col transition-colors duration-200">
          {isLoadingDetail ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="w-8 h-8 border-4 border-slate-200 border-t-purple-655 rounded-full animate-spin"></div>
            </div>
          ) : selectedItem ? (
            <div className="p-6 md:p-8 space-y-6 max-w-4xl w-full mx-auto animate-fade-in">
              {/* Top Banner: Controls & Meta */}
              <Card className="border border-slate-200 dark:border-slate-800 shadow-sm rounded-2xl overflow-hidden bg-white dark:bg-slate-900">
                <CardContent className="p-6 space-y-5">
                  <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 dark:border-slate-800/80 pb-4">
                    <div className="space-y-1.5 flex-1 min-w-[250px]">
                      <div className="flex items-center gap-2">
                        <span className={cn(
                          "text-[9px] font-bold px-3 py-1 rounded-full uppercase tracking-wider",
                          getFirmStyle(selectedItem.client_id).bg
                        )}>
                          {selectedItem.client_name} Client
                        </span>
                        <span className={cn("text-[9px] px-2 py-0.5 rounded-full font-bold", getPriorityBadgeStyle(selectedItem.priority))}>
                          {selectedItem.priority} Priority
                        </span>
                        <span className={cn("text-[9px] px-2.5 py-0.5 rounded-full font-bold", getStatusBadgeStyle(selectedItem.status))}>
                          {selectedItem.status}
                        </span>
                      </div>
                      <h2 className="text-lg md:text-xl font-extrabold text-slate-900 dark:text-white leading-tight">
                        {selectedItem.subject}
                      </h2>
                    </div>
                  </div>

                  {/* Operational Settings Fields (Interactable Fields) */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                    <div className="space-y-1.5">
                      <span className="text-slate-400 dark:text-slate-500 font-semibold flex items-center gap-1">
                        <User className="w-3.5 h-3.5 text-purple-500" /> Assigned Operator
                      </span>
                      <select
                        value={selectedItem.operator_id || ""}
                        onChange={(e) => handleUpdateField({ operatorId: e.target.value || null })}
                        className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-250 dark:border-slate-700 rounded-xl px-3 py-2 font-bold text-slate-850 dark:text-slate-150 focus:outline-none focus:ring-1 focus:ring-purple-650 cursor-pointer"
                      >
                        <option value="">Unassigned (Queue)</option>
                        {operators.map(op => (
                          <option key={op.id} value={op.id}>{op.full_name} ({op.role})</option>
                        ))}
                      </select>
                    </div>

                    <div className="space-y-1.5">
                      <span className="text-slate-400 dark:text-slate-500 font-semibold flex items-center gap-1">
                        <CheckCircle className="w-3.5 h-3.5 text-purple-500" /> Triage Status
                      </span>
                      <select
                        value={selectedItem.status}
                        onChange={(e) => handleUpdateField({ status: e.target.value })}
                        className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-250 dark:border-slate-700 rounded-xl px-3 py-2 font-bold text-slate-850 dark:text-slate-150 focus:outline-none focus:ring-1 focus:ring-purple-650 cursor-pointer"
                      >
                        <option value="New">New</option>
                        <option value="Assigned">Assigned</option>
                        <option value="In Progress">In Progress</option>
                        <option value="Waiting">Waiting</option>
                        <option value="Completed">Completed</option>
                        <option value="Archived">Archived</option>
                      </select>
                    </div>

                    <div className="space-y-1.5">
                      <span className="text-slate-400 dark:text-slate-500 font-semibold flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5 text-purple-500" /> SLA (Resolution Limit)
                      </span>
                      <div className="bg-slate-50 dark:bg-slate-800 border border-slate-250 dark:border-slate-700 rounded-xl px-3 py-2 font-bold text-slate-850 dark:text-slate-150 flex items-center justify-between">
                        <span>{getSLACountdown(selectedItem.sla)}</span>
                        <span className="text-[10px] text-slate-400 font-medium">({formatDateTime(selectedItem.sla).split(",")[0]})</span>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-slate-100 dark:border-slate-800/80 pt-4 text-xs">
                    <div>
                      <span className="text-slate-400 font-medium">Sender:</span>
                      <span className="ml-1.5 font-bold text-slate-700 dark:text-slate-300">{selectedItem.sender_email}</span>
                    </div>
                    <div>
                      <span className="text-slate-400 font-medium">Associated Matter:</span>
                      <span className="ml-1.5 font-bold text-slate-700 dark:text-slate-350">{selectedItem.matter_name || selectedItem.matter_id || "Unspecified Matter"}</span>
                    </div>
                  </div>

                  {/* Attachment Cards */}
                  {selectedItem.attachments && selectedItem.attachments.length > 0 && (
                    <div className="space-y-2 border-t border-slate-100 dark:border-slate-800/80 pt-4">
                      <h4 className="text-xs font-bold text-slate-450 uppercase tracking-wider flex items-center gap-1.5">
                        <Paperclip className="w-3.5 h-3.5 text-purple-550" />
                        Ingested Attachments ({selectedItem.attachments.length})
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {selectedItem.attachments.map(att => (
                          <div 
                            key={att.id} 
                            onClick={() => handleDownloadFile(att)}
                            className="p-3 bg-slate-50 dark:bg-slate-800/60 border border-slate-150 dark:border-slate-800 hover:border-purple-300 dark:hover:border-purple-900 rounded-xl flex items-center justify-between gap-3 cursor-pointer group transition-all"
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <FileText className="w-5 h-5 text-purple-500 flex-shrink-0" />
                              <div className="min-w-0 text-left">
                                <p className="text-xs font-bold text-slate-700 dark:text-slate-200 truncate group-hover:text-purple-650 transition-colors">
                                  {att.filename}
                                </p>
                                <p className="text-[10px] text-slate-400">
                                  {att.file_size ? `${Math.ceil(att.file_size / 1024)} KB` : "Size Unknown"}
                                </p>
                              </div>
                            </div>
                            <Download className="w-4 h-4 text-slate-400 group-hover:text-purple-600 transition-colors" />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Sub-view Tabs Menu */}
              <div className="flex border-b border-slate-200 dark:border-slate-800 gap-2">
                <button
                  onClick={() => setActiveDetailTab("preview")}
                  className={cn(
                    "px-4 py-2.5 text-xs font-bold border-b-2 transition-all flex items-center gap-1.5",
                    activeDetailTab === "preview"
                      ? "border-purple-600 text-purple-655"
                      : "border-transparent text-slate-500 hover:text-slate-700"
                  )}
                >
                  <MailOpen className="w-3.5 h-3.5" />
                  Email Body
                </button>
                <button
                  onClick={() => setActiveDetailTab("audit")}
                  className={cn(
                    "px-4 py-2.5 text-xs font-bold border-b-2 transition-all flex items-center gap-1.5",
                    activeDetailTab === "audit"
                      ? "border-purple-600 text-purple-655"
                      : "border-transparent text-slate-500 hover:text-slate-700"
                  )}
                >
                  <ShieldCheck className="w-3.5 h-3.5" />
                  Audit & Events Timeline
                  <Badge className="text-[9px] px-1 py-0 bg-slate-100 text-slate-655 font-medium ml-1">
                    {(selectedItem.events?.length || 0) + (selectedItem.history?.length || 0)}
                  </Badge>
                </button>
                <button
                  onClick={() => setActiveDetailTab("comments")}
                  className={cn(
                    "px-4 py-2.5 text-xs font-bold border-b-2 transition-all flex items-center gap-1.5",
                    activeDetailTab === "comments"
                      ? "border-purple-600 text-purple-655"
                      : "border-transparent text-slate-500 hover:text-slate-700"
                  )}
                >
                  <MessageSquare className="w-3.5 h-3.5" />
                  Operator Comments
                  <Badge className="text-[9px] px-1 py-0 bg-slate-100 text-slate-655 font-medium ml-1">
                    {selectedItem.comments?.length || 0}
                  </Badge>
                </button>
              </div>

              {/* Sub-view: Email Preview */}
              {activeDetailTab === "preview" && (
                <Card className="border border-slate-200 dark:border-slate-800 shadow-sm rounded-2xl bg-white dark:bg-slate-900 overflow-hidden">
                  <CardContent className="p-6">
                    <div className="bg-slate-50 dark:bg-slate-950/60 rounded-xl p-6 border border-slate-150 dark:border-slate-800/80 font-sans text-xs text-slate-800 dark:text-slate-350 leading-relaxed whitespace-pre-wrap overflow-x-auto min-h-[150px]">
                      {selectedItem.body || (
                        <span className="text-slate-400 italic">No message body ingested.</span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Sub-view: Audit and Workflow Event timeline */}
              {activeDetailTab === "audit" && (
                <Card className="border border-slate-200 dark:border-slate-800 shadow-sm rounded-2xl bg-white dark:bg-slate-900">
                  <CardContent className="p-6 space-y-6">
                    <div>
                      <h4 className="text-xs font-extrabold text-slate-850 dark:text-slate-250 uppercase tracking-wider mb-3">
                        Automated Portal Workflow steps
                      </h4>
                      <div className="relative border-l border-slate-200 dark:border-slate-750 pl-4 ml-2 space-y-4">
                        {selectedItem.events && selectedItem.events.map((evt, idx) => (
                          <div key={evt.id || idx} className="relative">
                            <span className="absolute -left-[21.5px] top-1.5 w-2 h-2 rounded-full bg-purple-500 border border-white dark:border-slate-900 shadow-sm"></span>
                            <div className="text-xs">
                              <p className="font-semibold text-slate-750 dark:text-slate-200">{evt.description}</p>
                              <span className="text-[10px] text-slate-450 font-medium">Status: {evt.status}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="border-t border-slate-100 dark:border-slate-800 pt-5">
                      <h4 className="text-xs font-extrabold text-slate-855 dark:text-slate-250 uppercase tracking-wider mb-3">
                        Manual Operator Action History
                      </h4>
                      <div className="relative border-l border-slate-200 dark:border-slate-750 pl-4 ml-2 space-y-4">
                        {selectedItem.history && selectedItem.history.map((hist, idx) => (
                          <div key={hist.id || idx} className="relative">
                            <span className="absolute -left-[21.5px] top-1.5 w-2 h-2 rounded-full bg-slate-450 border border-white dark:border-slate-900 shadow-sm"></span>
                            <div className="text-xs">
                              <div className="flex items-center gap-2">
                                <span className="font-bold text-slate-700 dark:text-slate-200">{hist.action}</span>
                                <span className="text-[9px] text-slate-400">by {hist.operator_id}</span>
                              </div>
                              <p className="text-slate-500 dark:text-slate-400 text-[11px] mt-0.5">{hist.details}</p>
                              <span className="text-[9px] text-slate-400 font-medium">{formatDateTime(hist.created_at)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Sub-view: Chat and Comments */}
              {activeDetailTab === "comments" && (
                <div className="space-y-4">
                  {/* Chat logs */}
                  <Card className="border border-slate-200 dark:border-slate-800 shadow-sm rounded-2xl bg-white dark:bg-slate-900">
                    <CardContent className="p-6 space-y-4 max-h-[350px] overflow-y-auto">
                      {selectedItem.comments && selectedItem.comments.length > 0 ? (
                        selectedItem.comments.map((com, idx) => (
                          <div key={com.id || idx} className="flex gap-2.5 items-start">
                            <div className="w-8 h-8 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-purple-650 dark:text-purple-400 font-bold text-xs flex-shrink-0">
                              {com.user_name.split(" ").map(w => w[0]).join("").toUpperCase()}
                            </div>
                            <div className="flex-1 bg-slate-50 dark:bg-slate-950 p-3 rounded-xl border border-slate-100 dark:border-slate-800">
                              <div className="flex items-center justify-between text-[10px] text-slate-400 font-medium mb-1">
                                <span className="font-bold text-slate-655 dark:text-slate-300">{com.user_name}</span>
                                <span>{formatDateTime(com.created_at)}</span>
                              </div>
                              <p className="text-xs text-slate-700 dark:text-slate-300 leading-relaxed whitespace-pre-wrap">{com.comment_text}</p>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-10 text-slate-400 dark:text-slate-500 text-xs">
                          <MessageSquare className="w-8 h-8 text-slate-350 dark:text-slate-700 mx-auto mb-2" />
                          No operational comments posted yet.
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Comment Form */}
                  <form onSubmit={handleAddComment} className="flex gap-2.5">
                    <input
                      type="text"
                      placeholder="Add an operational update note..."
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      disabled={isSubmittingComment}
                      className="flex-1 px-4 py-2.5 border border-slate-200 dark:border-slate-850 rounded-xl text-sm bg-white dark:bg-slate-900 text-slate-850 dark:text-slate-150 focus:outline-none focus:ring-2 focus:ring-purple-650 shadow-inner"
                    />
                    <button
                      type="submit"
                      disabled={isSubmittingComment || !commentText.trim()}
                      className="bg-purple-600 hover:bg-purple-700 disabled:bg-purple-450 dark:disabled:bg-purple-900 text-white font-bold text-xs px-5 py-2.5 rounded-xl shadow-md transition-colors"
                    >
                      Post Note
                    </button>
                  </form>
                </div>
              )}
            </div>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center p-8 text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-white dark:bg-slate-900 border border-slate-150 dark:border-slate-800 flex items-center justify-center text-slate-400 shadow-sm">
                <MailOpen className="w-7 h-7 text-purple-600/60" />
              </div>
              <div className="max-w-xs space-y-1.5">
                <h3 className="text-base font-bold text-slate-800 dark:text-slate-205">No Ingested Request Selected</h3>
                <p className="text-xs text-slate-400 dark:text-slate-500">
                  Select a submission from the list on the left to read its full message details, review attached documents, trigger workflows, and assign operators.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
